/**
 * Final Phase 1 decisions (New Joinee Home)
 *
 * Q1 Form access: New Joinee Form first; the other 6 unlock once it is submitted
 *    (see isJoineeFormUnlocked)
 * Q2 Missing fields: show "--"
 * Q3 Submit: removed — each form submits individually; HR reviews per form
 * Q4 NDA → Home; no final home Submit (avoids candidate confusion)
 * Q5 Upload Profile: dedicated POST /user/:id/profile-photo + circular crop (react-easy-crop)
 * Q6 Labels: keep Figma "From F" / "From Q"
 * Q7 Paragraphs: flag later per form
 * Q8 Routes: "/" = Home; New Joinee form = "/new-joinee"; keep other form paths
 * Q9 Header: same Auth Quick Help for all joinee pages
 * Q10 Acknowledgments: defer to form phase
 */
export var JOINEE_DISPLAY_EMPTY = "--";

export var JOINEE_FORM_LIST = [
  {
    id: "newJoinee",
    name: "New Joinee Form",
    statusKey: "isNewJoineeFormComplete",
    commentKey: "newJoineeFormComment",
    path: "/new-joinee",
    showSectionProgress: true,
  },
  {
    id: "sdf",
    name: "Self Declaration",
    statusKey: "isSelfDecalrationFormComplete",
    commentKey: "selfDecalrationFormComment",
    path: "/sdf",
  },
  {
    id: "epf",
    name: "EPF Form",
    statusKey: "isEPFFormComplete",
    commentKey: "EPFFormComment",
    path: "/epf",
  },
  {
    id: "form11",
    name: "Form 11",
    statusKey: "isForm11Complete",
    commentKey: "form11Comment",
    path: "/form11",
  },
  {
    id: "formf",
    name: "From F",
    statusKey: "isFormFComplete",
    commentKey: "formFComment",
    path: "/formf",
  },
  {
    id: "formq",
    name: "From Q",
    statusKey: "isFormQComplete",
    commentKey: "FormQComment",
    path: "/formq",
  },
  {
    id: "nda",
    name: "NDA",
    statusKey: "isNDAFormComplete",
    commentKey: "NDAFormComment",
    path: "/nda",
  },
];

/**
 * Candidate still needs to complete this form.
 * Note: user schema defaults statuses to "pending" = not started.
 */
export function isJoineeFormIncomplete(status) {
  return (
    status === "pending" ||
    status === "draft" ||
    status === "resubmission required" ||
    !status
  );
}

/**
 * Candidate has submitted this form (HR may still review).
 */
export function isJoineeFormDone(status) {
  return (
    status === "submitted" ||
    status === "review done" ||
    status === "resubmitted"
  );
}

/**
 * The New Joinee Form has been submitted at least once. A later HR
 * resubmission request does not lock the other forms again.
 */
export function isNewJoineeFormSubmitted(status) {
  return isJoineeFormDone(status) || status === "resubmission required";
}

/** Only the New Joinee Form is open until it is submitted. */
export function isJoineeFormUnlocked(user, form) {
  if (!form || form.id === "newJoinee") {
    return true;
  }
  return isNewJoineeFormSubmitted(user && user.isNewJoineeFormComplete);
}

/** HR has approved the form. */
export function isJoineeFormApproved(status) {
  return status === "review done";
}

/**
 * Candidate submitted and waiting for HR decision.
 */
export function isJoineeFormAwaitingHr(status) {
  return status === "submitted" || status === "resubmitted";
}

/**
 * Employee must not edit after submit/approval until HR requests resubmission.
 */
export function isJoineeEmployeeFormLocked(status) {
  return (
    status === "submitted" ||
    status === "resubmitted" ||
    status === "review done"
  );
}

/** Show HR Approve only while awaiting review — not after approval. */
export function canShowHrApprove(status) {
  return isJoineeFormAwaitingHr(status);
}

/**
 * Allow HR to request resubmission while awaiting review or after approval.
 */
export function canShowHrResubmit(status) {
  return isJoineeFormAwaitingHr(status) || isJoineeFormApproved(status);
}

/** Any HR review chrome (Approve and/or Re-submit). */
export function canShowHrReviewActions(status) {
  return canShowHrApprove(status) || canShowHrResubmit(status);
}

export function joineeFormStatusLabel(status) {
  if (!status || status === "pending") {
    return "Not started";
  }
  if (status === "draft") {
    return "Draft — continue filling";
  }
  if (status === "resubmission required") {
    return "Action needed — resubmit";
  }
  if (status === "submitted" || status === "resubmitted") {
    return "Completed — with HR for review";
  }
  if (status === "review done") {
    return "Approved by HR";
  }
  return String(status);
}

export function formatJoineeValue(value) {
  if (value === null || value === undefined || value === "") {
    return JOINEE_DISPLAY_EMPTY;
  }
  return String(value);
}

export function getJoineeFormComment(user, form) {
  if (!user || !form || !form.commentKey) {
    return "";
  }
  var comment = user[form.commentKey];
  return comment ? String(comment).trim() : "";
}
